import { Button, Icon, Heading, colors, Input, Spinner } from "@thetreep/ui";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router";
import { useNavigate } from "react-router";
import { Field } from "./Field";
import { PlaceInput } from "./PlaceInput";
import { PlaceType } from "@gen/place/v1/place_pb";
import { ConnectError } from "@connectrpc/connect";
import { useToaster } from "@hooks/useToaster";
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  useForm,
} from "react-hook-form";
import Select from "./Select";
import { NumberInput } from "@components/form/NumberInput";
import { SelectToggle } from "./SelectToggle";

interface DetailsViewProps<T extends FieldValues> {
  item?: T;
  title?: string;
  fields: Field<T>[];
  isLoading?: boolean;
  isEditable: boolean;
  onSubmit: (item: T) => Promise<void>;
}

export function DetailsView<T extends FieldValues>({
  item,
  title,
  fields,
  isLoading,
  isEditable,
  onSubmit,
}: DetailsViewProps<T>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToaster();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSubmiting, setIsSubmiting] = useState(false);
  const { control, getValues, reset, handleSubmit, watch } = useForm<T>();
  const watchedItem = watch();

  useEffect(() => {
    reset(item, { keepDirty: true });
    if (isSubmiting) {
      setIsSubmiting(false);
      setIsEditing(false);
    }
  }, [item]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!item) {
    return <div>{t("notFound")}</div>;
  }

  async function onSubmitClick() {
    setIsSubmiting(true);
    try {
      await onSubmit(getValues());
      setIsEditing(false);
    } catch (e) {
      if (e instanceof ConnectError) {
        addToast("error", e.rawMessage);
      }
    }
    setIsSubmiting(false);
  }

  const isEditing = searchParams.has("edit");

  const setIsEditing = (edit: boolean) => {
    if (edit) {
      setSearchParams({ ...searchParams, edit: "" });
    } else {
      searchParams.delete("edit");
      setSearchParams(searchParams);
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full mt-2 p-4">
      <div
        className="flex text-primary-strong cursor-pointer"
        onClick={() =>
          navigate(location.pathname.split("/").slice(0, -1).join("/"))
        }
      >
        <Icon src="left_line" color={colors.primaryStrong} />
        <span>{t("back")}</span>
      </div>
      <div className="flex justify-between items-center">
        {title && <Heading size="l">{title}</Heading>}
        {!isEditing && isEditable && (
          <Button
            kind="outline"
            size="mini"
            colors={{
              color: colors.primaryStrong,
              backgroundColor: colors.primaryStrong,
            }}
            startEnhancer={<Icon src="pencil_line" color="#0C419A" />}
            onClick={() => setIsEditing(true)}
          >
            {t("edit")}
          </Button>
        )}
      </div>
      <div className="bg-neutral-50 border rounded-md p-4">
        <div className="grid grid-cols-2 gap-6 p-6">
          {fields.map((f, i) => (
            <Controller
              key={i}
              control={control}
              name={f.key}
              rules={f.creation?.rules}
              disabled={
                !f.editable ||
                !isEditing ||
                (f.condition && !f.condition?.(watchedItem))
              }
              render={({ field, fieldState }) => (
                <EditableField
                  key={i}
                  field={field}
                  config={f}
                  fieldState={fieldState}
                />
              )}
            />
          ))}
          {isEditing && (
            <div className="col-span-2 flex justify-end">
              <Button
                isLoading={isSubmiting}
                onClick={handleSubmit(onSubmitClick)}
              >
                {t("save")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface EditableFieldProps<T extends FieldValues> {
  field: ControllerRenderProps<T>;
  fieldState: ControllerFieldState;
  config: Field<T>;
}

function EditableField<T extends FieldValues>({
  field,
  fieldState,
  config,
}: EditableFieldProps<T>) {
  const { t } = useTranslation();

  function displayValue(): ReactNode {
    if (config.display) {
      return config.display(field.value);
    } else if (config.options) {
      if (Array.isArray(field.value)) {
        return field.value
          .map(
            (v: T[keyof T]) =>
              config.options?.find((o) => o.value === v)?.label,
          )
          .join(", ");
      } else {
        return (
          config.options.find((o) => o.value === field.value)?.label ||
          field.value
        );
      }
    } else {
      return field.value;
    }
  }

  const label = config.label ? t(config.label) : t(config.key as string);

  // view mode
  if (field.disabled) {
    return (
      <div className={`flex flex-col gap-2 col-span-${config.span}`}>
        <span className="text text-xs font-manrope">{label}</span>
        <span className="text-text-strong">{displayValue()}</span>
      </div>
    );
  }

  // edit mode
  let input: ReactNode;
  switch (config.type) {
    case "text":
      input = (
        <Input
          value={field.value}
          label={label}
          onChange={field.onChange}
          error={fieldState.invalid}
          errorMsg={fieldState.error?.message}
        ></Input>
      );
      break;
    case "number":
      input = (
        <NumberInput
          value={field.value}
          label={label}
          min={config.min}
          max={config.max}
          // comply to react-hook-form wanting empty strings instead of undefined
          onChange={(v) => field.onChange(v !== undefined ? v : "")}
          error={fieldState.error?.message}
        />
      );
      break;
    case "select":
      input = (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-bold">{label}</span>
          <Select
            multiple={config.multi}
            options={config.options || []}
            value={field.value}
            onChange={field.onChange}
            error={fieldState.invalid}
            errorMsg={fieldState.error?.message}
            optional
          />
        </div>
      );
      break;
    case "place":
      input = (
        <PlaceInput
          value={field.value}
          onChange={field.onChange}
          filter={[PlaceType.CITY]}
        />
      );
      break;
    case "select-toggle":
      input = (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-bold">{config.label}</span>
          <SelectToggle
            field={config}
            value={field.value}
            onChange={field.onChange}
          />
        </div>
      );
      break;

    default:
      input = <Input value={field.value} label={label}></Input>;
  }

  return (
    <div className={`flex flex-col gap-2 col-span-${config.span}`}>{input}</div>
  );
}
